import PropTypes from 'prop-types';
import React, { Component } from 'react';
import cx from 'classnames';

import Card from './Card';
import styles from './SimpleTooltip.css';

/**
 * Note: Set css position as relative of the parent element where this component is used.
 */
export default class SimpleTooltip extends Component {
	static propTypes = {
		anchor: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		hoverInTimeout: PropTypes.number,
		hoverOutTimeout: PropTypes.number,
		// If lazy prop is passed, the child is mounted only if it's active (hovered).
		// This ensures we are not mounting DOM nodes before they are shown. This is
		// useful when there are lot of tooltip children getting loaded in DOM even if
		// most of them won't be used, like in Search Browse page rating widget on
		// each product. Use this sparingly because creating complex DOM nodes on
		// demand can be very performance-intensive and cause jank.
		lazy: PropTypes.bool
	};

	static defaultProps = {
		position: 'top',
		lazy: false
	};

	constructor(props) {
		super(props);

		this.state = {
			active: false
		};
	}

	componentDidMount() {
		const { anchor } = this.props;

		this.anchorEl = typeof anchor === 'string' ? document.querySelector(anchor) : anchor;

		if (!this.anchorEl || this.props.manual) return;

		this.anchorEl.addEventListener('mouseenter', this.handleMouseEnter);
		this.anchorEl.addEventListener('mouseleave', this.handleMouseLeave);
	}

	componentWillUnmount() {
		this.hoverInTimer && clearTimeout(this.hoverInTimer);

		if (!this.anchorEl || this.props.manual) return;

		this.anchorEl.removeEventListener('mouseenter', this.handleMouseEnter);
		this.anchorEl.removeEventListener('mouseleave', this.handleMouseLeave);
	}

	show = () => this.handleShow(); // for backward API

	hide = (...args) => this.handleHide(...args); // for backward API

	handleShow = () => {
		this.setState({
			active: true
		});
	};

	handleHide = (withCallback = true) => {
		this.setState({
			active: false
		});
		if (withCallback && typeof this.props.onClose === 'function') {
			this.props.onClose();
		}
	};

	handleMouseEnter = e => {
		if (this.hoverOutTimer) clearTimeout(this.hoverOutTimer);

		if (!this.isVisible) {
			this.hoverInTimer = setTimeout(() => this.handleShow(), this.props.hoverInTimeout);
		}
	};

	handleMouseLeave = e => {
		if (this.hoverInTimer) clearTimeout(this.hoverInTimer);

		if (this.state.active && !this.mouseOverTooltip) {
			this.hoverOutTimer = setTimeout(() => this.handleHide(), this.props.hoverOutTimeout);
		}
	};

	render() {
		const { active } = this.state;
		const { className, lazy, containerClassName, activeClassName, ...props } = this.props;

		if (lazy && !active) {
			return null;
		}

		return (
			<Card
				{...props}
				active={active}
				onDismiss={this.handleHide}
				manualStyle
				className={cx(styles['simple-tooltip'], styles[props.position], { [styles['visible']]: active }, className)}
				containerClassName={cx(styles['container'], containerClassName)}
				activeClassName={activeClassName}
			/>
		);
	}
}
